import React from 'react';
import { useTranslation } from 'localization';
import type { AuditEntry } from 'types/channel';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import AuditEntryMolecule from '../AuditEntryMolecule/AuditEntryMolecule';
import './ItemAuditTabMolecule.scss';

export interface ItemAuditTabMoleculeProps {
  entries: AuditEntry[];
  hasMore: boolean;
  loading: boolean;
  error?: string | null;
  onLoadMore: () => void;
  className?: string;
}

const ItemAuditTabMolecule: React.FC<ItemAuditTabMoleculeProps> = ({
  entries,
  hasMore,
  loading,
  error = null,
  onLoadMore,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = ['marketplace-item-audit-tab-molecule', className]
    .filter(Boolean)
    .join(' ');

  const isInitialLoad = loading && entries.length === 0;
  const isEmpty = !loading && !error && entries.length === 0;

  if (error && entries.length === 0) {
    return (
      <div
        className={classes}
        data-testid="item-audit-error"
        role="alert"
      >
        {t('marketplace.itemDetail.audit.loadError')}
      </div>
    );
  }

  if (isInitialLoad) {
    return (
      <div
        className={classes}
        data-testid="item-audit-loading"
        role="status"
      >
        {t('marketplace.itemDetail.audit.loading')}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div
        className={classes}
        data-testid="item-audit-empty"
      >
        <p className="marketplace-item-audit-tab-molecule__empty">
          {t('marketplace.itemDetail.audit.empty')}
        </p>
      </div>
    );
  }

  return (
    <div className={classes} data-testid="item-audit-tab">
      <ol className="marketplace-item-audit-tab-molecule__list">
        {entries.map((entry) => (
          <AuditEntryMolecule key={entry.id} entry={entry} />
        ))}
      </ol>

      {hasMore && (
        <div className="marketplace-item-audit-tab-molecule__footer">
          <ButtonAtom
            variant="secondary"
            shape="pill"
            size="small"
            label={t('marketplace.itemDetail.audit.loadMore')}
            onClick={onLoadMore}
            disabled={loading}
            data-testid="item-audit-load-more"
          />
        </div>
      )}
    </div>
  );
};

ItemAuditTabMolecule.displayName = 'ItemAuditTabMolecule';

export default ItemAuditTabMolecule;
